import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import { productService } from '../services/productService'; 

const { FiPlus, FiTrash2, FiCheck, FiShoppingCart, FiX, FiMoreVertical, FiEdit2, FiFilter, FiList, FiRefreshCw } = FiIcons;

const CATEGORIES = [
  'Obst & Gemüse',
  'Milch & Käse',
  'Fleisch & Fisch',
  'Kühl & Tiefkühl',
  'Vorrat & Gewürze',
  'Brot & Gebäck',
  'Getränke',
  'Snacks & Süßes',
  'Haushalt & Drogerie',
  'Sonstiges'
];

export default function Shopping() {
  const { state, dispatch } = useFamilyContext();
  const { user, familyId } = useAuth();
  const { shoppingLists, shoppingItems } = state;

  const [activeListId, setActiveListId] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('Sonstiges');
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [addingItem, setAddingItem] = useState(false);

  useEffect(() => {
    if (!activeListId && shoppingLists && shoppingLists.length > 0) {
      setActiveListId(shoppingLists[0].id);
    } else if (activeListId && shoppingLists.length > 0 && !shoppingLists.find(l => l.id === activeListId)) {
      setActiveListId(shoppingLists[0].id);
    }
  }, [shoppingLists, activeListId]);

  const handleNameBlur = async () => {
    if (newItemName.trim() && (newItemCategory === 'Sonstiges' || !newItemCategory)) {
      const cat = await productService.categorize(newItemName);
      if (cat && CATEGORIES.includes(cat)) {
        setNewItemCategory(cat);
      }
    }
  };

  const handleCreateList = async (e) => {
    e.preventDefault();
    if (!newListName.trim()) return;
    
    const newListId = uuidv4();
    dispatch({ 
      type: 'ADD_SHOPPING_LIST', 
      payload: {
        id: newListId,
        name: newListName,
        family_id: familyId,
        created_by: user.id
      } 
    });
    
    setActiveListId(newListId);
    setNewListName('');
    setIsCreatingList(false);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemName.trim() || !activeListId || addingItem) return;

    setAddingItem(true);
    try {
      let finalCategory = newItemCategory;
      if (finalCategory === 'Sonstiges') {
        const autoCat = await productService.categorize(newItemName);
        if (autoCat && CATEGORIES.includes(autoCat)) finalCategory = autoCat;
      }

      dispatch({
        type: 'ADD_SHOPPING_ITEM',
        payload: {
          id: uuidv4(),
          list_id: activeListId,
          listId: activeListId,
          name: newItemName,
          category: finalCategory,
          completed: false,
          added_by: user.id,
          family_id: familyId
        }
      });
      setNewItemName('');
      setNewItemCategory('Sonstiges');
    } finally {
      setAddingItem(false);
    }
  };

  const handleClearList = () => {
    if (window.confirm('Möchten Sie alle Artikel aus dieser Liste löschen?')) {
      dispatch({ type: 'CLEAR_SHOPPING_LIST', payload: activeListId });
    }
  };

  const groupedItems = useMemo(() => {
    const items = (shoppingItems || []).filter(i => i.list_id === activeListId || i.listId === activeListId);
    const groups = {};
    CATEGORIES.forEach(c => groups[c] = []);
    
    items.forEach(item => {
      let cat = item.category;
      if (!CATEGORIES.includes(cat)) cat = 'Sonstiges';
      groups[cat].push(item);
    });
    return groups;
  }, [shoppingItems, activeListId]);

  const activeList = shoppingLists.find(l => l.id === activeListId);

  return (
    <div className="h-[calc(100vh-100px)] flex flex-col md:flex-row gap-6">
      {/* List Sidebar */}
      <div className="w-full md:w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
          <button
            onClick={() => setIsCreatingList(true)}
            className="w-full flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-bold shadow-sm active:scale-95"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" /> Neue Liste
          </button>
        </div>

        {isCreatingList && (
          <form onSubmit={handleCreateList} className="p-4 bg-blue-50 dark:bg-blue-900/20 space-y-2">
            <input
              autoFocus
              value={newListName}
              onChange={e => setNewListName(e.target.value)}
              placeholder="Name..."
              className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
            <div className="flex gap-2">
              <button type="submit" className="flex-1 bg-blue-600 text-white py-1.5 rounded-lg text-xs font-bold">OK</button>
              <button onClick={() => setIsCreatingList(false)} className="flex-1 bg-white dark:bg-gray-800 border py-1.5 rounded-lg text-xs dark:text-gray-300">X</button>
            </div>
          </form>
        )}

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {shoppingLists.map(list => (
            <div
              key={list.id}
              onClick={() => setActiveListId(list.id)}
              className={`group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${
                activeListId === list.id 
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' 
                  : 'hover:bg-gray-50 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center">
                <SafeIcon icon={FiShoppingCart} className="w-4 h-4 mr-3" />
                <span className="font-bold text-sm truncate">{list.name}</span>
              </div>
              <button
                onClick={e => { e.stopPropagation(); dispatch({ type: 'DELETE_SHOPPING_LIST', payload: list.id }); }}
                className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 rounded-lg"
              >
                <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Items Area */}
      <div className="flex-1 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">
        {activeListId ? (
          <>
            <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center bg-gray-50/30 dark:bg-gray-900/20">
              <div className="flex items-center">
                <h2 className="text-xl font-bold dark:text-white mr-4">{activeList?.name}</h2>
                <button 
                  onClick={handleClearList}
                  className="flex items-center text-xs text-red-500 hover:text-red-600 font-bold bg-red-50 dark:bg-red-900/20 px-3 py-1.5 rounded-lg transition-colors"
                  title="Alle Artikel aus dieser Liste löschen"
                >
                  <SafeIcon icon={FiRefreshCw} className="w-3 h-3 mr-1.5" /> Liste leeren
                </button>
              </div>
              <div className="text-xs font-bold px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 rounded-full">
                {Object.values(groupedItems).flat().filter(i => !i.completed).length} Artikel
              </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <form onSubmit={handleAddItem} className="flex flex-col md:flex-row gap-2">
                <input
                  required
                  value={newItemName}
                  onChange={e => setNewItemName(e.target.value)}
                  onBlur={handleNameBlur}
                  placeholder="Was wird benötigt?"
                  className="flex-1 p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <div className="flex gap-2">
                  <select
                    value={newItemCategory}
                    onChange={e => setNewItemCategory(e.target.value)}
                    className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button type="submit" className="px-6 bg-blue-600 text-white rounded-xl font-bold shadow-sm active:scale-95">
                    <SafeIcon icon={FiPlus} className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {Object.entries(groupedItems).map(([category, items]) => {
                if (items.length === 0) return null;
                return (
                  <div key={category} className="space-y-2">
                    <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">{category}</h3>
                    <div className="space-y-1">
                      {items.map(item => (
                        <div key={item.id} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${item.completed ? 'bg-gray-50 dark:bg-gray-900/20 border-transparent opacity-60' : 'bg-white dark:bg-gray-700 border-gray-100 dark:border-gray-600 hover:shadow-sm'}`}>
                          <div className="flex items-center flex-1 cursor-pointer" onClick={() => dispatch({ type: 'UPDATE_SHOPPING_ITEM', payload: { ...item, completed: !item.completed } })}>
                            <div className={`w-5 h-5 rounded-lg border mr-3 flex items-center justify-center ${item.completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 dark:border-gray-500'}`}>
                              {item.completed && <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />}
                            </div>
                            <span className={`text-sm font-medium ${item.completed ? 'line-through text-gray-500' : 'dark:text-white'}`}>{item.name}</span>
                          </div>
                          <button onClick={() => dispatch({ type: 'DELETE_SHOPPING_ITEM', payload: item.id })} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
            <SafeIcon icon={FiList} className="w-12 h-12 mb-4 opacity-20" />
            <p className="font-bold">Wähle eine Liste aus oder erstelle eine neue.</p>
          </div>
        )}
      </div>
    </div>
  );
}